import React from 'react';
import {
  Icon,
  IconButton,
  Input,
  Select,
  SelectOption,
  Stack,
  TextBody,
  Tooltip,
} from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { NodeColumns, tableStore } from '../../stores/table-store';
import {
  VOICE_INBOX_HANDOFF_TYPE,
  type VoiceHandoffType,
} from '../../utils/voice-handoff-types';

function getWorkflowSkillTags(): string[] {
  return [
    ...new Set(
      tableStore.rows
        .map((row) => row[NodeColumns.skillTag])
        .filter((tag): tag is string => Boolean(tag))
    ),
  ];
}

export const SkillTagSelect: React.FC<{
  value: string;
  isGeneral: boolean;
  usedSkillTags: string[];
  testId: string;
  onChange: (value: string) => void;
}> = observer(({ value, isGeneral, usedSkillTags, testId, onChange }) => {
  if (isGeneral) {
    return (
      <Input
        variant="outlined"
        value={value}
        disabled
        test-id={testId}
        style={{ width: '100%' }}
      >
        <Tooltip
          slot="suffix"
          placement="right"
          content="If no matching skill is found, the system uses the default general skill."
        >
          <Icon name="info" color="gray" size="xsmall" />
        </Tooltip>
      </Input>
    );
  }

  const usedSet = new Set(usedSkillTags);
  const options = [
    ...new Set([...(value ? [value] : []), ...getWorkflowSkillTags()]),
  ].filter(
    (skill) => skill !== 'general' && (skill === value || !usedSet.has(skill))
  );

  return (
    <Select
      variant="outlined"
      value={value}
      placeholder="Select skill"
      test-id={testId}
      style={{ width: '100%' }}
      onChange={(e) => onChange(e.target.value as string)}
    >
      {options.map((skill) => (
        <SelectOption key={skill} value={skill}>
          {skill}
        </SelectOption>
      ))}
    </Select>
  );
});

export const AddHandoffRuleButton: React.FC<{
  usedSkillTags: string[];
  onAdd: () => void;
}> = observer(({ usedSkillTags, onAdd }) => {
  const availableSkills = getWorkflowSkillTags().filter(
    (skill) => skill !== 'general' && !usedSkillTags.includes(skill)
  );
  const disabled = availableSkills.length === 0;

  const button = (
    <IconButton
      name="plus"
      size="small"
      variant="secondary"
      rounded
      disabled={disabled}
      test-id="ps-voice-channel-add-handoff-rule-button"
      onClick={onAdd}
    />
  );

  return (
    <Stack alignItems="center" gutter="xsmall">
      {disabled ? (
        <Tooltip
          content="Add skills in Workflows to enable more handoff rules."
          placement="right"
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <TextBody size="small">Add routing rule</TextBody>
    </Stack>
  );
});

/**
 * Where a skill's escalation goes.
 *
 * `voice_inbox` is the Contact Center destination and is offered only while a
 * Contact Center connection is selected: the option is meaningless without one
 * (Engagement resolves the inbox from the channel's integration info, which only
 * a connection supplies), and leaving it selectable afterwards would let a
 * channel keep a rule that routes nowhere. When the connection is removed the
 * caller rewrites such rules back to `phone_number` — see the form.
 */
export const HandoffTypeSelect: React.FC<{
  value: VoiceHandoffType;
  testId: string;
  /** Whether a Contact Center connection is selected on this channel. */
  allowVoiceInbox: boolean;
  onChange: (value: VoiceHandoffType) => void;
}> = ({ value, testId, allowVoiceInbox, onChange }) => (
  <Select
    variant="outlined"
    value={value}
    test-id={testId}
    style={{ minWidth: 150 }}
    onChange={(e) => onChange(e.target.value as VoiceHandoffType)}
  >
    <SelectOption value="phone_number">
      <Icon name="call" size="xsmall" slot="prefix" />
      Phone number
    </SelectOption>
    <SelectOption value="sip">
      <Icon name="link" size="xsmall" slot="prefix" />
      SIP
    </SelectOption>
    {/* Rendered when allowed OR when it is the current value: a `ps-select`
        derives its displayed label from the selected OPTION, so dropping the
        option out from under a rule that holds it would blank the field and
        hide what the rule actually does. */}
    {(allowVoiceInbox || value === VOICE_INBOX_HANDOFF_TYPE) && (
      <SelectOption value={VOICE_INBOX_HANDOFF_TYPE}>
        <Icon name="users" size="xsmall" slot="prefix" />
        Contact Center
      </SelectOption>
    )}
  </Select>
);
